/**
 * S3 — Trial Status Detail (drawer/modal)
 *
 * Opened from multi-feature banner "View details" or profile-menu chip.
 * Lists all 5 gated features with state + expiry date.
 * Footer CTA by role: Admin → "Upgrade →", Non-admin → "Notify admin"
 *
 * States shown: Trial · 4d left | ✓ Active | 🔒 Premium | Expired
 *
 * Figma: S3 — Trial Status Detail
 */
import React from "react";
import type { FeatureFlagState } from "../../hooks/useFeatureFlags";

interface TrialStatusDetailProps {
  flags: FeatureFlagState[];
  isAdmin: boolean;
  onClose: () => void;
  onUpgradeClick: () => void;
  onNotifyAdmin: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export const TrialStatusDetail: React.FC<TrialStatusDetailProps> = ({
  flags,
  isAdmin,
  onClose,
  onUpgradeClick,
  onNotifyAdmin,
}) => {
  // All 5 gated features in order
  const ALL_FEATURES = ["ai_scribe", "whatsapp_notifications", "abdm_sync", "cloud_storage", "advanced_reports"];

  const featureRows = ALL_FEATURES.map(feat => {
    const flag = flags.find(f => f.feature === feat);
    return {
      feature: feat,
      label: flag?.display_label || feat.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      icon: flag?.icon || "⚡",
      flag,
    };
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[420px] w-full p-4 sm:p-6 relative">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
          aria-label="Close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-4 sm:mb-5 pr-6">Subscription & trial status</h2>

        {/* Feature list */}
        <div className="space-y-2 sm:space-y-3 mb-5 sm:mb-6">
          {featureRows.map(({ feature, label, icon, flag }) => {
            let badge: React.ReactNode;
            let subtitle: string;

            if (!flag || (!flag.is_enabled && !flag.is_trial)) {
              // Not subscribed / locked
              badge = (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <LockIcon /> Premium
                </span>
              );
              subtitle = "Not subscribed";
            } else if (flag.expired) {
              badge = (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                  <HourglassIcon /> Expired
                </span>
              );
              subtitle = `Ended ${formatDate(flag.expires_at)}`;
            } else if (flag.is_trial && flag.days_left !== null) {
              const urgent = flag.days_left <= 2;
              badge = (
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${
                  urgent ? "text-red-600 bg-red-50 border-red-200" : "text-blue-700 bg-blue-50 border-blue-200"
                }`}>
                  <HourglassIcon /> Trial · {flag.days_left}d left
                </span>
              );
              subtitle = `Ends ${formatDate(flag.expires_at)}`;
            } else if (flag.is_enabled) {
              badge = (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                  <CheckIcon /> Active
                </span>
              );
              subtitle = "Active subscription";
            } else {
              badge = (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <LockIcon /> Premium
                </span>
              );
              subtitle = "Not subscribed";
            }

            return (
              <div key={feature} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-0">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-slate-900 truncate">{label}</p>
                  <p className="text-[11px] sm:text-xs text-slate-500">{subtitle}</p>
                </div>
                {badge}
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <button
          onClick={() => { onClose(); isAdmin ? onUpgradeClick() : onNotifyAdmin(); }}
          className="w-full py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
        >
          {isAdmin ? "Upgrade →" : "Notify admin"}
        </button>
      </div>
    </div>
  );
};

const HourglassIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
  </svg>
);

const LockIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default TrialStatusDetail;
